import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from '../../../../components/common/Toast/toast';
import { createTrainerLeave, deleteTrainerLeave } from "../../../../../services/TrainerLeaveService.js";
import { getTrainers } from "../../../../../services/TrainerService.js";
import { getLeavesList } from "../../../../../services/ClassroomManagementService.js";
import { useAuth } from "../../../../../context/AuthContext.js";
import NewCustomDataTable from "../../../../components/common/NewCustomDataTable.jsx";
import useDebouncedCallback from "../../../../components/common/useDebouncedCallback";
import Modal from "../../components/Modal.jsx";
import StatusChip from "../../components/StatusChip.jsx";
import Pager from "../../components/Pager.jsx";
import AsyncPagedSelect from "../../components/AsyncPagedSelect.jsx";
import { todayDateStr } from "../../classroomSchedule.js";
import { confirm as confirmDialog } from '../../../../components/common/Confirm/confirm';
import LeaveRecordModal from "./LeaveRecordModal.jsx";

const PAGE_SIZE = 20;
const DASH = "\u2014";

const dateLabel = (iso) => (iso ? new Date(iso).toLocaleDateString() : DASH);

const REASONS = [
  "Sick leave",
  "Personal / family emergency",
  "Sanctioned leave",
  "Training / meeting",
  "Absent without notice",
  "Other",
];

const trainerDisplayName = (trainer) =>
  trainer?.trainerName || trainer?.name || trainer?.fullName || trainer?.trainerEmail || "Unknown";

const leaveStatus = (leave) => {
  const today = todayDateStr();
  const from = (leave.fromDate || "").slice(0, 10);
  const to = (leave.toDate || "").slice(0, 10);
  if (today < from) return { key: "upcoming", label: "Upcoming" };
  if (today > to) return { key: "past", label: "Past" };
  return { key: "absent", label: "Absent now" };
};

const TrainerLeavesSection = () => {
  const { hasPermission } = useAuth();
  const canEdit = hasPermission("list-trainer", ["update"], "lms");

  const [leaves, setLeaves] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uiSearchTerm, setUiSearchTerm] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ trainerId: "", fromDate: "", toDate: "", reason: REASONS[0], note: "" });
  // The leave being read, or null. The row is the whole record -- there
  // is no detail endpoint behind a leave -- so the popup opens with it.
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const result = await getLeavesList(page, PAGE_SIZE, search);
      setLeaves(result.data || []);
      setPagination(result.pagination);
    } catch (error) {
      console.error("Error loading trainer leaves:", error);
      toast.error("Failed to load trainer leaves");
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  useEffect(() => { load(); }, [load]);

  const runSearch = useDebouncedCallback((value) => {
    setSearch(value.trim());
    setPage(1);
  });

  /* The record popup stays up under the confirm (which sits above it) and
     closes only after the change: on success it shows the done panel and
     closes itself; on cancel or error it is still there. */
  const handleDelete = async (id) => {
    const confirm = await confirmDialog({
      title: "Remove this leave?",
      confirmButtonText: "Yes, remove it",
      tone: 'danger',
    });
    if (!confirm.isConfirmed) return;
    try {
      await deleteTrainerLeave(id);
      toast.success("Leave removed");
      load();
    } catch (error) {
      toast.error("Failed to remove leave");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.trainerId || !form.fromDate || !form.toDate) {
      toast.error("Please fill all required fields");
      return;
    }
    if (form.toDate < form.fromDate) {
      toast.error("To date must be on or after from date");
      return;
    }
    setSaving(true);
    try {
      await createTrainerLeave({
        ...form,
        fromDate: new Date(form.fromDate).toISOString(),
        toDate: new Date(form.toDate).toISOString(),
      });
      toast.success("Trainer leave recorded");
      setForm({ trainerId: "", fromDate: "", toDate: "", reason: REASONS[0], note: "" });
      load();
    } catch (error) {
      toast.error(error?.response?.data?.message?.message || "Failed to record leave");
    } finally {
      setSaving(false);
    }
  };

  /* No actions column: the row opens the record and ending the leave
     lives in that popup's footer, the way every other list here works
     (view_popup_rules). It used to sit on the row -- one click from
     ending someone's leave, beside a note clipped to fit. */
  const headers = useMemo(
    () => [
      {
        title: "Trainer",
        prop: "trainerName",
        isSortable: true,
        cell: (row) => <span className="pg-strong">{row.trainerName || DASH}</span>,
      },
      {
        title: "Status",
        prop: "fromDate",
        cell: (row) => {
          const status = leaveStatus(row);
          return <StatusChip status={status.key === "absent" ? "absent" : ""}>{status.label}</StatusChip>;
        },
      },
      {
        title: "From",
        prop: "fromDate",
        isSortable: true,
        cell: (row) => <span className="pg-date">{dateLabel(row.fromDate)}</span>,
      },
      {
        title: "To",
        prop: "toDate",
        isSortable: true,
        cell: (row) => <span className="pg-date">{dateLabel(row.toDate)}</span>,
      },
      {
        title: "Reason",
        prop: "reason",
        isSortable: true,
        cell: (row) => <span className="lp-plain">{row.reason || DASH}</span>,
      },
      {
        title: "Note",
        prop: "note",
        cell: (row) =>
          row.note ? <span className="lp-plain">{row.note}</span> : <span className="pg-none">{DASH}</span>,
      },
    ],
    [],
  );

  return (
    <section className="pg">
      <header className="pg-head">
        <div className="pg-head-main">
          <h2 className="pg-title">Trainer leaves &amp; absences</h2>
          <p className="pg-sub">
            Who is away, for how long, and why their sessions are not running.
          </p>
        </div>

        {canEdit && (
          <div className="pg-head-actions">
            <button
              type="button"
              className="pg-btn pg-btn--main"
              onClick={() => setShowModal(true)}
            >
              Mark trainer on leave
            </button>
          </div>
        )}
      </header>

      <section className="pg-filters" aria-label="Filter leaves">
        <div className="pg-filters-search">
          <div className="pg-field pg-field--search">
            <label className="pg-label" htmlFor="leave-search">Search</label>
            <input
              id="leave-search"
              type="search"
              className="pg-input pg-input--search"
              placeholder="Search by reason..."
              value={uiSearchTerm}
              onChange={(e) => {
                setUiSearchTerm(e.target.value);
                runSearch(e.target.value);
              }}
            />
          </div>
        </div>
      </section>

      {!loading && leaves.length === 0 ? (
        <div className="pg-state">
          <h3 className="pg-state-title">
            {search ? "Nothing matched" : "Nobody is on leave"}
          </h3>
          <p className="pg-state-text">
            {search
              ? `No leave matches \u201C${search}\u201D.`
              : "Every trainer is available; no absences have been recorded."}
          </p>
        </div>
      ) : (
        <>
          <div className="td-list td-list--leaves">
            <NewCustomDataTable
              data={leaves}
              headers={headers}
              totalRecords={pagination?.total ?? leaves.length}
              currentPage={page}
              recordsPerPage={PAGE_SIZE}
              serverSide
              loading={loading}
              showToolbar={false}
              showPagination={false}
              showFooter={false}
              onRowClick={(row) => setDetail(row)}
              validRowClickTagNames="*"
              rowProps={(row) => ({
                tabIndex: 0,
                role: "button",
                "aria-label": `Open the leave for ${row.trainerName || "this trainer"}`,
                onKeyDown: (e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setDetail(row);
                  }
                },
              })}
            />
          </div>

          <Pager pagination={pagination} onPageChange={setPage} />
        </>
      )}

      {detail && (
        <LeaveRecordModal
          record={detail}
          status={leaveStatus(detail)}
          canEdit={canEdit}
          onClose={() => setDetail(null)}
          onEnd={(row) => handleDelete(row.id)}
        />
      )}

      {showModal && (
        <Modal
          eyebrow="Leave"
          title="Mark a trainer on leave"
          sub="Who is away, between which dates, and why."
          onClose={() => setShowModal(false)}
          size="form"
          labelledBy="cls-leave-title"
          footer={
            <>
              <div className="pgm-actions">
                <button type="button" className="pgm-btn" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
              </div>
              <button
                type="submit"
                form="cls-leave-form"
                className="pg-btn pg-btn--main"
                disabled={saving}
              >
                {saving ? "Saving..." : "Save leave"}
              </button>
            </>
          }
        >
          <form id="cls-leave-form" className="att-form" noValidate onSubmit={submit}>
            <div className="pg-field">
              <label className="pg-label" htmlFor="lv-trainer">Trainer</label>
              <div id="lv-trainer">
                <AsyncPagedSelect
                  value={form.trainerId}
                  onChange={(id) => setForm((f) => ({ ...f, trainerId: id }))}
                  fetchPage={(pageNo, q) => getTrainers(20, pageNo, q)}
                  toOption={(t) => ({ value: t.id, label: trainerDisplayName(t) })}
                  placeholder="Select trainer"
                />
              </div>
            </div>

            <div className="mcq-options">
              <div className="pg-field">
                <label className="pg-label" htmlFor="lv-from">From</label>
                <input
                  id="lv-from"
                  type="date"
                  className="pg-input"
                  value={form.fromDate}
                  onChange={(e) => setForm((f) => ({ ...f, fromDate: e.target.value }))}
                />
              </div>
              <div className="pg-field">
                <label className="pg-label" htmlFor="lv-to">To</label>
                <input
                  id="lv-to"
                  type="date"
                  className="pg-input"
                  value={form.toDate}
                  onChange={(e) => setForm((f) => ({ ...f, toDate: e.target.value }))}
                />
              </div>
              <div className="pg-field">
                <label className="pg-label" htmlFor="lv-reason">Reason</label>
                <select
                  id="lv-reason"
                  className="pg-select"
                  value={form.reason}
                  onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))}
                >
                  {REASONS.map((r) => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
              </div>
              <div className="pg-field">
                <label className="pg-label" htmlFor="lv-note">Note</label>
                <input
                  id="lv-note"
                  className="pg-input"
                  value={form.note}
                  onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
                  placeholder="Optional"
                />
              </div>
            </div>
          </form>
        </Modal>
      )}
    </section>
  );
};

export default TrainerLeavesSection;
